import { log } from "./log.js";
/**
 * GestureController — shared lifecycle for editor pointer-driven gestures.
 *
 * Three editor controllers share the same shape: pointerdown engages a
 * gesture, pointermove updates live state, pointerup commits, Escape (or
 * pointer-capture-loss) cancels. Before this base, each one re-implemented
 * the lifecycle separately and DRY-drift caused regressions (e.g.
 * insert-flow's marquee never had Escape-cancel; pointer-capture-loss left
 * dangling state in drag-move). One base class owns the lifecycle so a
 * fix lands in one place.
 *
 * # Inventory of the three call-site contexts (motivates the design)
 *
 * The "context" is the per-gesture data the subclass carries from
 * pointerdown through pointerup. Each subclass keeps its own shape:
 *
 *   InsertFlowController        (web/static/editor/insert-flow.ts)
 *     context = { dragStart:{x,y}, lastSnap:Rect|null, marqueeEl:HTMLElement }
 *     side-effects: appends an .editor-insert-marquee div to the overlay
 *                   on engage; updates its position/snap classes on move;
 *                   removes it on commit OR cancel.
 *     commit:  emits a `commitInsert` op with the rubber-band rect.
 *     cancel:  removes the marquee; no op emitted.
 *
 *   DragMoveController          (web/static/editor/drag-move.ts)
 *     context = { origin:{x,y}, sourceId:string,
 *                 participants:Array<{structId, originalBbox, el,
 *                                     originalCssLeft, originalCssTop}> }
 *     side-effects: adds .is-dragging class to each participant's element
 *                   on engage; live-translates element CSS on move;
 *                   removes class + emits set-bbox ops on commit; restores
 *                   element CSS on cancel.
 *     commit:  emits one set-bbox op per moved participant.
 *     cancel:  restores each participant's CSS left/top to original.
 *
 *   ResizeMoveController        (web/static/editor/resize-move.ts)
 *     context = { dir:string, structId:string, originalBbox:number[],
 *                 boxEl:HTMLElement, originalCss:{left,top,width,height},
 *                 origin:{x,y} }
 *     side-effects: adds .is-resizing class on engage; live-resizes
 *                   element CSS on move; emits set-bbox op on commit;
 *                   restores element CSS + removes class on cancel.
 *     commit:  emits a single set-bbox op for the resized box.
 *     cancel:  restores element CSS to original; no op emitted.
 *
 * All three share the lifecycle (IDLE → ACTIVE → COMMITTED|CANCELLED),
 * pointer-capture acquisition + release, and "Escape during ACTIVE = cancel"
 * semantics. Each owns its own `context` shape because the side-effect
 * surface is genuinely different per gesture.
 *
 * # Pattern provenance
 *
 * Mirrors `EditorStateMachine` in `editor-lifecycle-state.ts`:
 *   frozen Enum + transition table + on_enter hooks + central dispatch.
 * See CLAUDE.md "Explicit state machines for async job lifecycles".
 */
// ─── Types ────────────────────────────────────────────────────────────────────
/**
 * The four gesture lifecycle states.
 *
 * IDLE       Before pointerdown OR after a previous gesture cleaned up.
 * ACTIVE     Engagement committed; pointermove drives live update.
 * COMMITTED  Pointerup arrived with a meaningful gesture (above threshold).
 *            Subclass `onCommit(context)` ran. Returns to IDLE on next
 *            transition (caller-driven via cleanup).
 * CANCELLED  Escape OR pointer-capture-loss aborted the gesture. Subclass
 *            `onCancel(context)` ran. Returns to IDLE on next transition.
 *
 * COMMITTED and CANCELLED are intermediate one-frame states — the
 * controller automatically transitions back to IDLE so a fresh gesture
 * can engage immediately. Tests can observe COMMITTED/CANCELLED via the
 * `_lastTerminal` test surface.
 */
export const GestureState = Object.freeze({
    IDLE: "IDLE",
    ACTIVE: "ACTIVE",
    COMMITTED: "COMMITTED",
    CANCELLED: "CANCELLED",
});
/** Typed transition table. */
const TRANSITIONS = Object.freeze({
    [GestureState.IDLE]: new Set([GestureState.ACTIVE]),
    [GestureState.ACTIVE]: new Set([GestureState.COMMITTED, GestureState.CANCELLED]),
    [GestureState.COMMITTED]: new Set([GestureState.IDLE]),
    [GestureState.CANCELLED]: new Set([GestureState.IDLE]),
});
// ─── Implementation ───────────────────────────────────────────────────────────
/**
 * Base class for editor pointer-driven gestures.
 *
 * Subclass override surface (all optional except onPointerDown):
 *
 *   onPointerDown(event) -> context | null
 *       Decide whether to engage. Returning null/undefined keeps the
 *       state machine in IDLE (no transition). Returning any non-null
 *       value engages the gesture; the value becomes `context` for the
 *       remaining lifecycle.
 *
 *   onPointerMove(context, event) -> void
 *       Called on every pointermove while ACTIVE. Live-update side
 *       effects (CSS, marquee position, etc.) belong here.
 *
 *   onCommit(context, event) -> void
 *       Called on pointerup. Subclass decides whether the gesture
 *       represents a meaningful change (e.g. above min-threshold) and
 *       fires the corresponding op. The state machine has already
 *       released pointer capture before this hook runs.
 *
 *   onCancel(context, event?) -> void
 *       Called on Escape, pointer-capture-loss, or explicit cancel().
 *       Subclass MUST clean up any live DOM artifacts (marquee div,
 *       is-dragging class, restored CSS) here so cancel paths and
 *       commit paths agree on the cleanup contract.
 *
 * Listeners attached by the base:
 *   captureElement.pointerdown   — engagement decision (capture-phase optional)
 *   captureElement.pointermove   — live update
 *   captureElement.pointerup     — commit
 *   captureElement.pointercancel — cancel (browser-driven capture loss)
 *   captureElement.lostpointercapture — cancel (capture released externally)
 *   document.keydown (capture)   — Escape -> cancel
 *
 * The base owns nothing about the meaningful work; it owns ONLY the
 * lifecycle, the listener wiring, and the pointer-capture acquire/release.
 */
export class GestureController {
    name;
    target;
    captureElement;
    document;
    _state;
    _context;
    _activePointerId;
    /** Test-only: most recent terminal state (COMMITTED|CANCELLED|null). */
    _lastTerminal;
    _destroyed;
    _usePointerDownCapture;
    _observeKeydown;
    _onPointerDown;
    _onPointerMove;
    _onPointerUp;
    _onPointerCancel;
    _onLostPointerCapture;
    _onKeyDown;
    constructor(opts) {
        if (!opts || !opts.target) {
            throw new Error("GestureController: target required");
        }
        this.name = opts.name || "gesture";
        this.target = opts.target;
        this.captureElement = opts.captureElement || opts.target;
        this.document = opts.document
            || this.target.ownerDocument
            || (typeof document !== "undefined" ? document : null);
        this._usePointerDownCapture = !!opts.usePointerDownCapture;
        this._observeKeydown = opts.observeKeydown !== false;
        this._state = GestureState.IDLE;
        this._context = null;
        this._activePointerId = null;
        this._lastTerminal = null;
        this._destroyed = false;
        // Bind once so add/remove pair on the SAME function reference.
        this._onPointerDown = this._handlePointerDown.bind(this);
        this._onPointerMove = this._handlePointerMove.bind(this);
        this._onPointerUp = this._handlePointerUp.bind(this);
        this._onPointerCancel = this._handlePointerCancel.bind(this);
        this._onLostPointerCapture = this._handleLostPointerCapture.bind(this);
        this._onKeyDown = this._handleKeyDown.bind(this);
        this._bindListeners();
    }
    // ─── Subclass override surface ───────────────────────────────────────────
    // Defaults are no-ops; subclasses override what they need. Kept as
    // instance methods (not abstract) so a forgotten override degrades to
    // a no-op rather than a crash.
    /** Return non-null context to engage; null/undefined to stay IDLE. */
    onPointerDown(_event) { return null; }
    onPointerMove(_context, _event) { }
    onCommit(_context, _event) { }
    onCancel(_context, _event) { }
    // ─── Public API ──────────────────────────────────────────────────────────
    get state() { return this._state; }
    /** Test surface — peek at the active context (null if not ACTIVE). */
    _getContextForTest() { return this._context; }
    /** Test surface — the most recent terminal state, or null. */
    _getLastTerminalForTest() { return this._lastTerminal; }
    /**
     * Programmatically cancel an in-flight gesture. No-op if not ACTIVE.
     * Used by host code on global navigations (e.g. page change).
     */
    cancel() {
        if (this._state !== GestureState.ACTIVE)
            return;
        this._transition(GestureState.CANCELLED, null);
    }
    /**
     * Detach all listeners. Idempotent. Cancels any active gesture so DOM
     * artifacts (marquee, is-dragging class) don't leak.
     */
    destroy() {
        if (this._destroyed)
            return;
        if (this._state === GestureState.ACTIVE) {
            this._transition(GestureState.CANCELLED, null);
        }
        this._unbindListeners();
        this._destroyed = true;
    }
    // ─── Listener plumbing ───────────────────────────────────────────────────
    _bindListeners() {
        const downOpts = this._usePointerDownCapture ? true : undefined;
        this.target.addEventListener("pointerdown", this._onPointerDown, downOpts);
        this.target.addEventListener("pointermove", this._onPointerMove);
        this.target.addEventListener("pointerup", this._onPointerUp);
        this.target.addEventListener("pointercancel", this._onPointerCancel);
        this.target.addEventListener("lostpointercapture", this._onLostPointerCapture);
        if (this._observeKeydown && this.document && this.document.addEventListener) {
            // Capture-phase so we beat the editor's other keydown listeners
            // (e.g. cards.ts arrow-key navigation) when a gesture is active.
            this.document.addEventListener("keydown", this._onKeyDown, true);
        }
    }
    _unbindListeners() {
        const downOpts = this._usePointerDownCapture ? true : undefined;
        this.target.removeEventListener("pointerdown", this._onPointerDown, downOpts);
        this.target.removeEventListener("pointermove", this._onPointerMove);
        this.target.removeEventListener("pointerup", this._onPointerUp);
        this.target.removeEventListener("pointercancel", this._onPointerCancel);
        this.target.removeEventListener("lostpointercapture", this._onLostPointerCapture);
        if (this._observeKeydown && this.document && this.document.removeEventListener) {
            this.document.removeEventListener("keydown", this._onKeyDown, true);
        }
    }
    // ─── Event handlers (lifecycle) ──────────────────────────────────────────
    _handlePointerDown(event) {
        if (this._state !== GestureState.IDLE) {
            // Re-entrant pointerdown while ACTIVE — ignore. State machine
            // rejects ACTIVE → ACTIVE; we'd prefer not to throw on a stray
            // pointerdown so fall through quietly.
            return;
        }
        let ctx = null;
        try {
            ctx = this.onPointerDown(event);
        }
        catch (err) {
            // Subclass threw during engagement — log and stay IDLE. We can't
            // safely transition without a context.
            log.warn(`[gesture:${this.name}] onPointerDown threw; staying IDLE:`, err);
            return;
        }
        if (ctx == null)
            return;
        // Engage. The transition handler acquires pointer capture.
        this._activePointerId = (event && event.pointerId !== undefined)
            ? event.pointerId : null;
        this._transition(GestureState.ACTIVE, ctx);
    }
    _handlePointerMove(event) {
        if (this._state !== GestureState.ACTIVE)
            return;
        try {
            this.onPointerMove(this._context, event);
        }
        catch (err) {
            log.warn(`[gesture:${this.name}] onPointerMove threw; cancelling:`, err);
            this._transition(GestureState.CANCELLED, event);
        }
    }
    _handlePointerUp(event) {
        if (this._state !== GestureState.ACTIVE)
            return;
        // Release capture BEFORE invoking onCommit so the subclass can do
        // anything it likes (e.g., focus a dialog) without fighting capture.
        this._releasePointerCapture();
        this._transition(GestureState.COMMITTED, event);
    }
    _handlePointerCancel(event) {
        if (this._state !== GestureState.ACTIVE)
            return;
        this._transition(GestureState.CANCELLED, event);
    }
    _handleLostPointerCapture(event) {
        // Browser took capture away from us (e.g. dev-tools, alert dialog,
        // page lost focus). Treat as cancel so the marquee/is-dragging class
        // doesn't leak.
        if (this._state !== GestureState.ACTIVE)
            return;
        this._transition(GestureState.CANCELLED, event);
    }
    _handleKeyDown(event) {
        if (this._state !== GestureState.ACTIVE)
            return;
        if (event && event.key === "Escape") {
            // Stop propagation so the SPA-level Escape (close modal, deselect)
            // doesn't ALSO fire on a gesture-cancellation. drag-move did this
            // before the refactor.
            if (typeof event.stopPropagation === "function")
                event.stopPropagation();
            this._transition(GestureState.CANCELLED, event);
        }
    }
    // ─── State transition + on_enter hooks ───────────────────────────────────
    /**
     * Transition the SM. on_enter side effects (pointer capture, subclass
     * commit/cancel hooks) fire here. Caller passes `ctx` only when
     * transitioning to ACTIVE; for COMMITTED/CANCELLED we use the existing
     * `_context` and clear it afterward.
     * @param next   Next state.
     * @param ctxOrEvent  Context when transitioning to ACTIVE; PointerEvent/KeyboardEvent/null otherwise.
     */
    _transition(next, ctxOrEvent) {
        const valid = TRANSITIONS[this._state];
        if (!valid || !valid.has(next)) {
            log.warn(`[gesture:${this.name}] illegal transition ${this._state} -> ${next} ` +
                `(valid: ${valid ? Array.from(valid).join(", ") : "<none>"})`);
            return;
        }
        this._state = next;
        if (next === GestureState.ACTIVE) {
            this._context = ctxOrEvent;
            this._acquirePointerCapture();
            return;
        }
        // For COMMITTED / CANCELLED we use the existing context and clear it
        // AFTER the subclass hook runs so subclass code can read it.
        const ctx = this._context;
        const event = ctxOrEvent;
        if (next === GestureState.COMMITTED) {
            this._lastTerminal = GestureState.COMMITTED;
            try {
                this.onCommit(ctx, event);
            }
            catch (err) {
                // Commit hook errors don't roll back state — the gesture
                // already happened from the user's POV. Subclass-level
                // failures (e.g. host onCommit handler) are logged.
                log.warn(`[gesture:${this.name}] onCommit threw:`, err);
            }
            this._cleanupAndReturnToIdle();
            return;
        }
        if (next === GestureState.CANCELLED) {
            this._lastTerminal = GestureState.CANCELLED;
            // Release capture BEFORE the subclass cancel hook so the
            // subclass can manipulate DOM freely.
            this._releasePointerCapture();
            try {
                this.onCancel(ctx, event);
            }
            catch (err) {
                log.warn(`[gesture:${this.name}] onCancel threw:`, err);
            }
            this._cleanupAndReturnToIdle();
            return;
        }
        // GestureState.IDLE — reached only through _cleanupAndReturnToIdle;
        // nothing to do beyond the state assignment already done above.
    }
    _cleanupAndReturnToIdle() {
        // Clear context + pointer id, then transition back to IDLE so the
        // controller is ready for the next gesture.
        this._context = null;
        this._activePointerId = null;
        // Force-transition without going through the table guard — IDLE is
        // always a legal successor of COMMITTED/CANCELLED.
        this._state = GestureState.IDLE;
    }
    _acquirePointerCapture() {
        if (this._activePointerId == null)
            return;
        const el = this.captureElement;
        if (!el || typeof el.setPointerCapture !== "function")
            return;
        try {
            el.setPointerCapture(this._activePointerId);
        }
        catch (_) { /* setPointerCapture throws if the pointer is no longer active; harmless */ }
    }
    _releasePointerCapture() {
        if (this._activePointerId == null)
            return;
        const el = this.captureElement;
        if (!el || typeof el.releasePointerCapture !== "function")
            return;
        try {
            el.releasePointerCapture(this._activePointerId);
        }
        catch (_) { /* already released or never acquired; harmless */ }
    }
}
